/* eslint-disable camelcase */
import { Box, Button, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import CountUp from 'react-countup'

import colors from '../../../../assets/colors'
import { renderRoute } from '../../../../util/utils'
import { CustomSwitch } from '../../../../components/ui/custom-switch'
import { SNACKBAR } from '../../../main/MainActions'
import {
  cancelAddon,
  contractAddon,
  fetchMyAddons,
  updateAddonQuantity,
} from '../../SubscriptionActions'

import StatusChip from './StatusChip'
import ConfirmModal from './ConfirmModal'
import QuantityDialog from './QuantityDialog'
import {
  ADDON_STATUS_CANCEL_SCHEDULED,
  ADDON_STATUS_CANCELED,
  ADDON_STATUS_TONE,
  BOLETO_PRORATA_HINT,
  BOLETO_PRORATA_NOTICE,
  accessQuantity,
  formatAddonDate,
  hasScheduledReduction,
  isBoletoMethod,
  scheduledQuantity,
  scheduledReductionLabel,
} from './addonHelpers'

const useStyles = makeStyles(() => ({
  tile: {
    border: `1px solid ${colors.graylight}`,
    borderRadius: '10px',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  tileInfo: {
    flex: 1,
    minWidth: 0,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '2px',
  },
  caption: {
    fontSize: '11px',
    color: colors.textGray,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  qtyInput: {
    width: '64px',
  },
}))

// junta contratados (com addon_service aninhado) e disponíveis (catálogo flat)
// numa lista única, uma entrada por addon_service. contract != null => ativo.
const buildAddonList = (contracted = [], available = []) => {
  const map = new Map()

  available.forEach(service => {
    map.set(service.id, { service, contract: null })
  })

  contracted.forEach(item => {
    const service = item.addon_service || {}
    const key =
      item.addon_service_id != null ? item.addon_service_id : service.id
    const isActive = item.status !== ADDON_STATUS_CANCELED

    if (isActive) {
      map.set(key, { service, contract: item })
    } else if (!map.has(key)) {
      map.set(key, { service, contract: null })
    }
  })

  return Array.from(map.values())
}

// quantidade escolhida ANTES de contratar: não dispara request nenhum, é o
// switch que contrata a quantidade final numa única chamada (e numa única
// cobrança de pró-rata)
const PendingQuantityInput = ({ value, onChange, label }) => {
  const classes = useStyles()

  return (
    <TextField
      className={classes.qtyInput}
      type="number"
      value={value}
      onChange={e => onChange(e.target.value)}
      onBlur={() => onChange(Math.max(1, parseInt(value, 10) || 1))}
      size="small"
      variant="outlined"
      title={label}
      inputProps={{
        min: 1,
        'aria-label': label,
        style: { textAlign: 'center', padding: '6px' },
      }}
    />
  )
}

const useQtyButtonStyles = makeStyles(() => ({
  qtyButton: {
    minWidth: '58px',
    padding: '2px 8px',
    fontSize: '13px',
    fontWeight: 700,
    color: colors.primary,
    borderColor: colors.graylight,
    textTransform: 'none',
  },
}))

// a quantidade em vigor é um botão: alterar abre o modal de confirmação, que
// explica o efeito (cobrança na hora ao aumentar, acesso até o fim do ciclo ao
// reduzir) antes de disparar a chamada
const QuantityButton = ({ contract, onClick }) => {
  const classes = useQtyButtonStyles()
  const scheduled = scheduledQuantity(contract)

  return (
    <Button
      variant="outlined"
      size="small"
      className={classes.qtyButton}
      onClick={onClick}
      title="Alterar quantidade"
    >
      {scheduled != null
        ? `${accessQuantity(contract)} → ${scheduled}`
        : `× ${accessQuantity(contract)}`}
    </Button>
  )
}

const AddonTile = ({
  name,
  price,
  originalPrice,
  description,
  meta,
  footer,
  children,
}) => {
  const classes = useStyles()

  const contUpMoney = money => {
    return (
      <CountUp
        end={money}
        duration={0.7}
        decimal=","
        decimals={2}
        prefix="R$"
        separator="."
      />
    )
  }

  // valores em centavos; quando o preço efetivo (price) é menor que o de
  // catálogo (originalPrice), exibe o valor cheio riscado ao lado do efetivo
  const hasDiscount = originalPrice != null && originalPrice > price

  return (
    <Box className={classes.tile}>
      <Box className={classes.tileInfo}>
        <Typography style={{ fontWeight: 700, fontSize: '14px' }} noWrap>
          {name}
        </Typography>
        {description && (
          <Typography className={classes.caption} noWrap title={description}>
            {description}
          </Typography>
        )}
        <Box className={classes.metaRow}>
          {hasDiscount ? (
            <Box
              style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}
            >
              <Typography
                style={{
                  fontSize: '11px',
                  color: colors.textGray,
                  textDecoration: 'line-through',
                }}
              >
                {contUpMoney(originalPrice / 100)}
              </Typography>
              <Typography
                style={{
                  fontSize: '12px',
                  color: colors.textGray,
                  fontWeight: 700,
                }}
              >
                {contUpMoney(price / 100)}
              </Typography>
            </Box>
          ) : (
            <Typography style={{ fontSize: '12px', color: colors.textGray }}>
              {contUpMoney(price / 100)}
            </Typography>
          )}
          {meta}
        </Box>
        {footer}
      </Box>
      {children}
    </Box>
  )
}

const Addons = () => {
  const classes = useStyles()
  const dispatch = useDispatch()

  const permissions = useSelector(state => state.auth.permissions)
  const canManage = renderRoute(['gerency-update-laundry-client'], permissions)

  const contractedAddons = useSelector(
    state => state.subscription.contractedAddons
  )
  const availableAddons = useSelector(
    state => state.subscription.availableAddons
  )
  const planDetails = useSelector(state => state.subscription.planDetails)
  const gatewayInfo = useSelector(state => state.subscription.gatewayInfo)

  // quem paga em boleto precisa ser avisado da cobrança avulsa do pró-rata:
  // o acesso é liberado na hora, mas um boleto é gerado junto
  const payInBoleto = isBoletoMethod(planDetails?.payment_method_id)
  const cycleEnd = formatAddonDate(gatewayInfo?.current_cycle?.end_at)

  // quantidade digitada antes de contratar, por serviço (default 1)
  const [contractQty, setContractQty] = useState({})
  // add-on em edição de quantidade: { service, contract }
  const [quantityDialog, setQuantityDialog] = useState(null)
  // confirmação em aberto (cancelamento), no padrão BaseFilterModal
  const [confirmRequest, setConfirmRequest] = useState(null)

  const qtyToContract = serviceId =>
    Math.max(1, parseInt(contractQty[serviceId], 10) || 1)

  useEffect(() => {
    dispatch(fetchMyAddons())
  }, [])

  // sucessos de contratação/aumento não trazem data na message da API, então
  // aqui trocamos o toast pelo aviso do boleto gerado (o snackbar é único)
  const notifyBoletoCharge = (ok, prefix) => {
    if (ok && payInBoleto) {
      dispatch({
        type: SNACKBAR.INFO,
        success: { message: `${prefix} ${BOLETO_PRORATA_NOTICE}` },
      })
    }
  }

  const handleContract = (addonServiceId, quantity) => {
    dispatch(contractAddon(addonServiceId, quantity)).then(ok => {
      notifyBoletoCharge(ok, 'Serviço adicional liberado.')
      if (ok) setContractQty(prev => ({ ...prev, [addonServiceId]: 1 }))
    })
  }

  const handleCancel = (contractId, name) => {
    setConfirmRequest({
      title: 'Cancelar serviço adicional',
      description: `O serviço "${name}" continua liberado até o fim do ciclo já pago${
        cycleEnd ? ` (${cycleEnd})` : ''
      } e sai da próxima fatura. Não há devolução do valor já pago neste ciclo.`,
      confirmationText: 'Cancelar serviço',
      danger: true,
      onConfirm: () => dispatch(cancelAddon(contractId)),
    })
  }

  const handleToggle = (entry, checked) => {
    if (checked) {
      const { service } = entry
      handleContract(
        service.id,
        service.type === 'quantity' ? qtyToContract(service.id) : 1
      )
      return
    }
    handleCancel(entry.contract.id, entry.service?.name)
  }

  // aumentar cobra o pró-rata na hora; reduzir vale sempre e só entra em vigor
  // no fim do ciclo pago (a message da API carrega a data e é exibida no toast)
  const handleUpdateQuantity = (contract, quantity) => {
    const isIncrease = quantity > accessQuantity(contract)

    setQuantityDialog(null)
    dispatch(updateAddonQuantity(contract.id, quantity)).then(ok => {
      if (isIncrease) notifyBoletoCharge(ok, 'Quantidade liberada.')
    })
  }

  const renderDiscount = contract => {
    if (contract.discount_percentage >= 100) {
      return <StatusChip label="Grátis" tone="success" />
    }
    if (contract.discount_percentage > 0) {
      return (
        <StatusChip label={`−${contract.discount_percentage}%`} tone="info" />
      )
    }
    return null
  }

  const addonList = buildAddonList(contractedAddons, availableAddons)
  const visibleList = canManage
    ? addonList
    : addonList.filter(entry => entry.contract)

  return (
    <Box>
      <Typography
        style={{
          fontWeight: 'bold',
          fontSize: '16px',
          color: colors.primary,
          margin: '4px 0 4px 0',
        }}
      >
        Serviços adicionais
      </Typography>

      {canManage && payInBoleto && (
        <Typography className={classes.caption} style={{ marginBottom: '8px' }}>
          {BOLETO_PRORATA_HINT}
        </Typography>
      )}

      {visibleList.map(entry => {
        const { service, contract } = entry
        const isQuantity = service.type === 'quantity'
        const price = contract ? contract.price_snapshot : service.price
        // status 25 = cancelamento agendado: continua ativo até o fim do ciclo,
        // mas o switch fica desligado para permitir recontratar (reativar).
        const isCancelScheduled =
          contract && contract.status === ADDON_STATUS_CANCEL_SCHEDULED

        // uma redução agendada mantém o acesso na quantidade antiga (já paga)
        // até quantity_change_at; o footer mostra as duas pontas
        const reductionFooter = hasScheduledReduction(contract) ? (
          <Typography className={classes.caption}>
            {scheduledReductionLabel(contract)} · a quantidade atual já está
            paga neste ciclo
          </Typography>
        ) : null

        const cancelFooter =
          contract && contract.cancel_requested_at ? (
            <Typography className={classes.caption}>
              Cancelamento solicitado em{' '}
              {formatAddonDate(contract.cancel_requested_at)}
              {contract.effective_cancel_at &&
                ` · acesso liberado até ${formatAddonDate(
                  contract.effective_cancel_at
                )}, sem devolução do valor já pago`}
            </Typography>
          ) : null

        return (
          <AddonTile
            key={`addon-${service.id}`}
            name={service.name}
            description={service.description}
            price={price}
            originalPrice={service.price}
            footer={
              <>
                {reductionFooter}
                {cancelFooter}
              </>
            }
            meta={
              <>
                {contract && (
                  <StatusChip
                    label={contract.status_text}
                    tone={ADDON_STATUS_TONE[contract.status] || 'neutral'}
                  />
                )}
                {contract && isQuantity && !canManage && (
                  <StatusChip
                    label={`× ${accessQuantity(contract)}`}
                    tone="neutral"
                  />
                )}
                {contract && renderDiscount(contract)}
                {!contract && isQuantity && (
                  <StatusChip
                    label="cobrado por unidade · informe a quantidade"
                    tone="neutral"
                  />
                )}
                {service.free_from_user_count && (
                  <StatusChip
                    label={`Grátis a partir de ${service.free_from_user_count} usuários`}
                    tone="success"
                  />
                )}
                {contract && contract.contracted_by === 'admin' && (
                  <Typography className={classes.caption}>
                    contratado pela equipe
                  </Typography>
                )}
              </>
            }
          >
            {canManage && (
              <Box className={classes.actions}>
                {contract && isQuantity && !isCancelScheduled && (
                  <QuantityButton
                    contract={contract}
                    onClick={() => setQuantityDialog({ service, contract })}
                  />
                )}
                {!contract && isQuantity && (
                  <PendingQuantityInput
                    value={contractQty[service.id] ?? 1}
                    onChange={qty =>
                      setContractQty(prev => ({ ...prev, [service.id]: qty }))
                    }
                    label={`quantidade a contratar de ${service.name}`}
                  />
                )}
                <CustomSwitch
                  checked={!!contract && !isCancelScheduled}
                  onChange={(e, checked) => handleToggle(entry, checked)}
                  inputProps={{
                    'aria-label': `contratar ${service.name}`,
                  }}
                />
              </Box>
            )}
          </AddonTile>
        )
      })}

      <ConfirmModal
        confirm={confirmRequest}
        onClose={() => setConfirmRequest(null)}
      />

      <QuantityDialog
        dialog={quantityDialog}
        payInBoleto={payInBoleto}
        cycleEnd={cycleEnd}
        onClose={() => setQuantityDialog(null)}
        onConfirm={qty => handleUpdateQuantity(quantityDialog.contract, qty)}
      />
    </Box>
  )
}

export default Addons
